import React, { useRef, useEffect } from "react";
import { RotateCcw, Search } from "lucide-react";
import "./index.css";
import {
  Allocations,
  Cart,
  PreOrders,
  ProductSKU,
} from "../../../../redux/distributionSlice";
import { Spinner } from "react-bootstrap";

interface GridViewProps {
  skus: ProductSKU[];
  carts: Cart[];
  allocations: Allocations;
  preOrders: PreOrders;
  selectedCells: Set<string>;
  searchTerm: string;
  cartSearch: string;
  setSearchTerm: (term: string) => void;
  setCartSearch: (term: string) => void;
  toggleCellSelection: (productItemId: string, cartId: string) => void;
  updateAllocation: (
    productItemId: string,
    cartId: string,
    value: number | string
  ) => void;
  getTotalAllocated: (id: string) => number;
  isLoading?: boolean;
  isReadOnly?: boolean;
  originalAllocations: Allocations;
  plannedAllocations?: Allocations;
}

const GridView: React.FC<GridViewProps> = ({
  skus,
  carts,
  allocations,
  preOrders,
  selectedCells,
  searchTerm,
  cartSearch,
  setSearchTerm,
  setCartSearch,
  toggleCellSelection,
  updateAllocation,
  getTotalAllocated,
  isLoading,
  isReadOnly,
  originalAllocations,
  plannedAllocations = {},
}) => {
  const bodyScrollRef = useRef<HTMLDivElement>(null);
  const headerScrollRef = useRef<HTMLDivElement>(null);

  // Calculate cart-wise totals
  const getCartTotals = () => {
    const cartTotals: { [cartId: string]: number } = {};
    
    carts.forEach(cart => {
      cartTotals[cart.id] = 0;
    });

    skus.forEach(sku => {
      carts.forEach(cart => {
        const allocation = allocations[sku.id]?.[cart.id] || 0;
        const preOrder = preOrders[cart.id]?.[sku.id] || 0;
        cartTotals[cart.id] += allocation + preOrder;
      });
    });

    return cartTotals;
  };

  // Calculate grand total
  const getGrandTotal = () => {
    const cartTotals = getCartTotals();
    return Object.values(cartTotals).reduce((sum, total) => sum + total, 0);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "High":
        return "priority-indicator-high";
      case "Medium":
        return "priority-indicator-medium";
      case "Low":
        return "priority-indicator-low";
      default:
        return "priority-indicator-default";
    }
  };

  const isValueModified = (productItemId: string, cartId: string) => {
    const currentValue = allocations[productItemId]?.[cartId] || 0;
    const originalValue = originalAllocations[productItemId]?.[cartId] || 0;
    return currentValue !== originalValue;
  };

  // Sync horizontal scrolling between header and body
  useEffect(() => {
    const bodyContainer = bodyScrollRef.current;
    const headerContainer = headerScrollRef.current;

    const handleBodyScroll = () => {
      if (bodyContainer && headerContainer) {
        headerContainer.scrollLeft = bodyContainer.scrollLeft;
      }
    };

    const handleHeaderScroll = () => {
      if (bodyContainer && headerContainer) {
        bodyContainer.scrollLeft = headerContainer.scrollLeft;
      }
    };

    if (bodyContainer) {
      bodyContainer.addEventListener("scroll", handleBodyScroll);
    }

    if (headerContainer) {
      headerContainer.addEventListener("scroll", handleHeaderScroll);
    }

    return () => {
      if (bodyContainer) {
        bodyContainer.removeEventListener("scroll", handleBodyScroll);
      }
      if (headerContainer) {
        headerContainer.removeEventListener("scroll", handleHeaderScroll);
      }
    };
  }, []);

  const cartTotals = getCartTotals();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const grandTotal = getGrandTotal();

  return (
    <div
      className={`allocation-grid-container ${
        isLoading ? "allocation-grid--loading" : ""
      }`}
    >
      {isLoading && (
        <div className="allocation-grid-loading-overlay">
          <Spinner className="allocation-grid-spinner" />
          <span>Loading Distribution Plan...</span>
        </div>
      )}

      <div className="bulk-edit-controls">
        <div className="grid-filter-controls">
          <div className="filter-input-group">
            <Search className="filter-input-icon" size={14} />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="filter-input-group">
            <Search className="filter-input-icon" size={14} />
            <input
              type="text"
              placeholder="Search carts..."
              value={cartSearch}
              onChange={(e) => setCartSearch(e.target.value)}
              disabled={isLoading}
            />
          </div>
        </div>
      </div>

      {/* Grid Table with synchronized horizontal scrolling */}
      <div className="grid-table-wrapper">
        {/* Header Container - Now scrollable horizontally */}
        <div className="grid-header-container" ref={headerScrollRef}>
          <div className="grid-header-content">
            <table className="allocation-data-table allocation-header-table">
              <thead>
                <tr>
                  <th className="sticky-product-column product-info-column header-product-cell">
                    Product
                  </th>
                  {carts.map((cart) => (
                    <th
                      key={cart.id}
                      className={`cart-column-header ${
                        cart.isOrderPlaced ? "order-placed-disabled" : ""
                      }`}
                      title={cart.name}
                    >
                      <div className="cart-header-details">
                        <span className="cart-identifier">
                          {cart.abbreviation && cart.abbreviation.trim() !== ""
                            ? cart.abbreviation
                            : cart.name}
                        </span>
                        <span
                          className={`priority-indicator ${getPriorityColor(
                            cart.priority
                          )}`}
                        >
                          {cart.priority}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
            </table>
          </div>
        </div>

        {/* Body Container - Scrollable both horizontally and vertically */}
        <div className="grid-body-container" ref={bodyScrollRef}>
          <div className="grid-body-content">
            <table className="allocation-data-table allocation-body-table">
              <tbody>
                {skus.map((sku) => {
                  const totalAllocated = getTotalAllocated(sku.id);
                  const restaurantReserved = sku.restaurantReserved || 0;
                  const totalCartReserved = sku.totalCartReserved || 0;

                  const availableQuantity =
                    (sku.totalProduced || 0) -
                    restaurantReserved -
                    totalCartReserved -
                    totalAllocated;

                  return (
                    <tr key={sku.id} className="product-data-row">
                      <td className="sticky-product-column product-details-cell">
                        <div className="product-details-container">
                          <div className="product-name-text" title={sku.name}>
                            {sku.abbreviation && sku.abbreviation.trim() !== ""
                              ? sku.abbreviation
                              : sku.name}
                          </div>
                          <div className="product-statistics">
                            <span>
                              {sku.portionSize && (
                                <span
                                  title={sku.portionSize}
                                  className="portion-size-badge"
                                >
                                  {sku.portionSize === "LARGE"
                                    ? "L"
                                    : sku.portionSize === "REGULAR"
                                    ? "R"
                                    : sku.portionSize === "SMALL"
                                    ? "S"
                                    : sku.portionSize}
                                </span>
                              )}
                            </span>
                            <span
                              className="total-quantity"
                              title="Total Produced"
                            >
                              TP: {sku.totalProduced.toLocaleString()}
                            </span>
                            <span
                              className="allocated-quantity"
                              title="Total Allocated"
                            >
                              TA: {totalAllocated.toLocaleString()}
                            </span>
                            <span
                              className="reserved-quantity"
                              title="Direct Orders"
                            >
                              DO: {restaurantReserved.toLocaleString()}
                            </span>
                            <span
                              className={`available-quantity ${
                                availableQuantity >= 0 ? "positive" : "negative"
                              }`}
                              title="Available Quantity"
                            >
                              AV: {availableQuantity.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </td>
                      {carts.map((cart) => {
                        const cellKey = `${sku.id}-${cart.id}`;
                        const currentAllocation =
                          allocations[sku.id]?.[cart.id] || 0;
                        const cartReservedQty =
                          preOrders[cart.id]?.[sku.id] || 0;
                        const plannedQty =
                          plannedAllocations[sku.id]?.[cart.id] || 0;
                        const isSelected = selectedCells.has(cellKey);
                        const isCartDisabled = cart.isOrderPlaced || isLoading;
                        const isModified = isValueModified(sku.id, cart.id);
                        const totalQty = currentAllocation + cartReservedQty;

                        return (
                          <td
                            key={cart.id}
                            className={`allocation-data-cell ${
                              isSelected ? "selected" : ""
                            } ${isCartDisabled ? "order-placed-disabled" : ""}`}
                            onClick={() =>
                              !isCartDisabled &&
                              toggleCellSelection(sku.id, cart.id)
                            }
                          >
                            <div className="cell-content-container">
                              <div
                                className={`allocation-input-wrapper ${
                                  isModified ? "allocation-input--modified" : ""
                                }`}
                              >
                                <input
                                  type="number"
                                  min="0"
                                  className={`allocation-quantity-input ${
                                    isCartDisabled
                                      ? "allocation-quantity-input--disabled"
                                      : ""
                                  }`}
                                  value={
                                    currentAllocation === 0
                                      ? ""
                                      : currentAllocation
                                  }
                                  placeholder={
                                    currentAllocation === 0 ? "0" : undefined
                                  }
                                  onChange={(e) =>
                                    !isCartDisabled &&
                                    updateAllocation(
                                      sku.id,
                                      cart.id,
                                      e.target.value
                                    )
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  disabled={isCartDisabled || isReadOnly}
                                />
                                {isModified && !isCartDisabled && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateAllocation(
                                        sku.id,
                                        cart.id,
                                        originalAllocations[sku.id]?.[
                                          cart.id
                                        ] || 0
                                      );
                                    }}
                                    className="allocation-reset-button"
                                    title="Reset to original value"
                                  >
                                    <RotateCcw
                                      className="allocation-reset-icon"
                                      size={10}
                                    />
                                  </button>
                                )}
                              </div>
                              <div className="quantity-details">
                                <span
                                  className="total-quantity-display"
                                  title="Total (Allocated + Pre-orders)"
                                >
                                  {totalQty}
                                </span>
                                <span
                                  className="planned-quantity"
                                  title="Planned Allocation"
                                >
                                  {plannedQty}
                                </span>
                                <span
                                  className="cart-reserved-quantity"
                                  title="Pre-orders"
                                >
                                  {cartReservedQty}
                                </span>
                              </div>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                
                {/* Cart-wise Total Row */}
                <tr className="cart-total-row">
                  <td className="sticky-product-column total-label-cell">
                    <div className="total-label-container">
                      <strong>Cart Total</strong>
                    
                    </div>
                  </td>
                  {carts.map((cart) => (
                    <td
                      key={cart.id}
                      className={`allocation-data-cell total-cell ${
                        cart.isOrderPlaced ? "order-placed-disabled" : ""
                      }`}
                    >
                      <div className="cart-total-container">
                        <strong className="cart-total-value">
                          {cartTotals[cart.id]?.toLocaleString() || "0"}
                        </strong>
                      </div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GridView;